import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { HiTrash } from 'react-icons/hi'
import { useApp } from '../../context/AppContext'
import FormError from '../feedback/FormError'
import adminServices from '../../services/AdminServices'

function CategoryModal({ action, activeCategory, onReload, setActiveCategory }) {
    const { setCategories } = useApp()
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [loading, setLoading] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState(0)

    const tabs = useMemo(() => {
        const base = ['Info']
        if (action === 1 || action === 2) base.push('Delete')
        return base
    }, [action])

    useEffect(() => {
        if ((action === 1 || action === 2) && activeCategory) {
            setName(activeCategory.name || '')
            setDescription(activeCategory.description || '')
        } else {
            setName('')
            setDescription('')
        }
        setActiveTab(0)
    }, [action, activeCategory])

    const updateSession = () => {
        adminServices.adminListCategories()
            .then(res => setCategories(res.data || []))
            .catch(err => console.warn('Session cache update failed', err))
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setError(null)
        setLoading(true)

        const payload = { name, description: description || '' }
        const request = action === 1
            ? adminServices.adminUpdateCategory(activeCategory.id, payload)
            : adminServices.adminCreateCategory(payload)

        request
            .then(res => {
                toast.success(action === 1 ? 'Category updated' : 'Category created')
                onReload?.()
                updateSession()
                closeModal()
            })
            .catch(err => {
                const msg = err.response?.data?.name?.[0]
                    || err.response?.data?.non_field_errors?.[0]
                    || 'Operation failed'
                setError(msg)
                toast.error(msg)
            })
            .finally(() => setLoading(false))
    }

    const handleDelete = () => {
        if (!activeCategory?.id) return
        setDeleteLoading(true)

        adminServices.adminDeleteCategory(activeCategory.id)
            .then(() => {
                toast.success('Category deleted')
                onReload?.()
                updateSession()
                closeModal()
            })
            .catch(err => {
                toast.error(err.response?.data?.detail || 'Delete failed')
            })
            .finally(() => setDeleteLoading(false))
    }

    const closeModal = () => {
        document.getElementById('category-modal-close-btn')?.click()
        setActiveCategory(null)
        setError(null)
    }

    const clearForm = () => {
        setName('')
        setDescription('')
        setActiveTab(0)
        setError(null)
    }

    return (
        <div>
            <label htmlFor="category-modal" id="category-open-btn" className="hidden">
                Category Modal
            </label>
            <input type="checkbox" id="category-modal" className="modal-toggle" onChange={clearForm} />

            <div className="modal modal-bottom sm:modal-middle">
                <div className="modal-box w-11/12 max-w-lg">
                    <label
                        htmlFor="category-modal"
                        id="category-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                        onClick={() => {
                            clearForm()
                            setActiveCategory(null)
                        }}
                    >
                        ✕
                    </label>

                    <h3 className="text-lg font-bold mb-4">
                        {action === 0 && 'Add Category'}
                        {action === 1 && `Edit: ${activeCategory?.name}`}
                        {action === 2 && `Delete: ${activeCategory?.name}`}
                    </h3>

                    {tabs.length > 1 && (
                        <div className="flex justify-center mb-6">
                            <div className="tabs tabs-boxed">
                                {tabs.map((tab, idx) => (
                                    <button
                                        key={idx}
                                        className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white' : ''}`}
                                        onClick={() => setActiveTab(idx)}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 0 && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && <FormError error={error} />}

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-semibold">Name *</span>
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="input input-bordered w-full focus:border-primary"
                                    placeholder="e.g. Executive Suite"
                                    disabled={loading}
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-semibold">Description</span>
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="textarea textarea-bordered w-full min-h-[80px] focus:border-primary"
                                    placeholder="Brief description for this category..."
                                    disabled={loading}
                                />
                            </div>

                            <div className="modal-action">
                                <button
                                    type="submit"
                                    disabled={loading || !name.trim()}
                                    className="btn btn-primary rounded-xl w-full"
                                >
                                    {loading ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : action === 1 ? 'Update Category' : 'Create Category'}
                                </button>
                            </div>
                        </form>
                    )}

                    {activeTab === 1 && action !== 0 && (
                        <div className="bg-error/5 border border-error/20 rounded-xl p-5">
                            <div className="flex items-start gap-3">
                                <HiTrash className="text-error h-7 w-7 mt-1" />
                                <div>
                                    <h4 className="font-bold text-error">Delete Category</h4>
                                    <p className="text-sm text-on-surface-variant/80 mt-1">
                                        This will remove <span className="font-semibold">{activeCategory?.name}</span>.
                                        Existing properties will lose this category assignment.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 flex gap-3">
                                <button
                                    onClick={() => setActiveTab(0)}
                                    className="btn btn-ghost btn-sm"
                                    disabled={deleteLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleteLoading}
                                    className="btn btn-error btn-sm gap-2"
                                >
                                    {deleteLoading ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        <>
                                            <HiTrash className="h-4 w-4" />
                                            Confirm Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CategoryModal